import { EMPTY_TEAM_COLORS, type TeamColors } from "@/utils/teamColors";
import { useCallback, useMemo, useRef, useState } from "react";
import { getCurrentAuth } from "../../../hooks/useAxiosPrivate";
import { refreshAuthToken } from "../../../utils/authRefreshInterceptor";
import { toast } from "react-toastify";
import { canDownloadFrom, permissionsFromToken } from "../../../utils/audienceLayerEntitlement";

// Re-exported: this hook's own tests, and any other existing consumer of this
// module, import permissionsFromToken from here. The function itself now
// lives in src/utils/audienceLayerEntitlement.ts, where the Studio page (v2)
// can reach it too, since src/v2 cannot import from src/Pages.
export { permissionsFromToken };

/**
 * The two broadcasts a game can be called from.
 *
 * Spelled the same way the Audience Layer and its API spell it, because the
 * string travels between all three: this page stamps it onto a Moment, the API
 * stores it, and the layer hands it back when the fan plays that Moment.
 */
export type AudienceLayerFeed = "home" | "away";

/**
 * How wide a clip's captured frame is stored.
 *
 * The tile it fills is 112 pixels across, so this is enough for any display
 * worth the pixels and small enough that the capture rides along inside the
 * create request instead of needing an upload of its own.
 */
const THUMBNAIL_WIDTH = 320;

/**
 * The contract the Audience Layer remote expects. Declared here rather than
 * imported: the remote is resolved at runtime by Module Federation, so the
 * host has no compile-time module to import types from, and a copy that
 * drifts is caught by the remote's own runtime validation.
 */
export interface AudienceLayerHostContext {
  gameId: string;
  tenant: string;
  user: {
    id: string;
    name: string;
    avatarUrl?: string;
    jwt: string;
    isModerator?: boolean;
  };
  device: string;
  apiBaseUrl?: string;
  /**
   * Whether the game is live right now, the player's own signal ORed with the
   * page's own flag. The layer cannot work this out and needs it to label a
   * control that offers the live edge back.
   */
  isLive: boolean;
  /**
   * A Moment the fan followed a link to, from the Studio's Clips tab.
   *
   * The layer opens its Moments panel on `tab` with `momentId` picked out.
   * Null on an ordinary visit, which is every visit that did not come from a
   * clip card.
   *
   * THE ID, NOT THE TITLE. The Watch page used to raise a banner naming the
   * clip, because the moments list beside it lists the game's key moments and
   * a clip a fan made is not one of them. Sending the id instead lets the
   * panel say it in the list the clip really lives in, and the banner is gone.
   *
   * `tab` is null when the link did not say, or said something this build does
   * not know: the panel opens on its own default rather than guessing, so a
   * link that outlives a rename still works.
   */
  clipTarget?: { momentId: string; tab: "mine" | "shared" | null } | null;
  /**
   * Whether this fan may download. A courtesy, not the gate: see the
   * useMemo that computes it below for why the tenant backend still runs
   * the real check on every request.
   */
  canDownload: boolean;
  /**
   * Which of the game's two broadcasts is on screen right now.
   *
   * A game can be called twice, once from each bench, and this page lets a fan
   * switch between them. A clip made while this says "away" is an away clip
   * and has to come back as one, so the layer stamps this onto every Moment it
   * creates. "home" for a game called once, which is most of them.
   */
  feed: AudienceLayerFeed;
  /**
   * The tenant's own game story for this game, as markdown, or null.
   *
   * PRE-game while the game is live and POST-game once it is not, decided by
   * the page because only the page knows which. Null covers both "nothing has
   * been written" and "it could not be loaded", which are the same thing to a
   * fan, and the layer renders one empty line for both.
   */
  gameSummary: string | null;
  /**
   * Which of the two stories `gameSummary` is.
   *
   * The layer heads the Story tab with it, and gets it rather than working it
   * out because only this page knows: the layer's own box score can say a game
   * is final while the page is still showing it as live, and a preview under
   * the word "Summary" reads as a report of a game nobody has played.
   */
  gameSummaryKind: "preview" | "summary";
  /**
   * The two clubs' brand colours, keyed by team abbreviation.
   *
   * The layer's Game tab reads its box score from GameSheet, which carries no
   * colours at all. These live on the tenant's own game record, which this
   * page already holds, so they travel down with the rest of the context
   * rather than costing the layer a second call to an API it does not own.
   *
   * Keyed by abbreviation and not by home and away on purpose: the colours
   * come from the tenant and the teams from GameSheet, and matching on
   * position would put one club's colour on the other's goals the first time
   * the two sources disagreed about which side is at home.
   */
  teamColors: TeamColors;
  /**
   * Say something to the fan, in this application's own voice.
   *
   * The layer is a 380px panel beside the video, and a message it renders
   * itself is a message inside that column. Anything worth interrupting
   * somebody for belongs where this app already puts them, which is the
   * app-global ToastContainer in App.tsx, top right, above everything.
   *
   * The layer decides WHAT to say and this decides WHERE it appears, which
   * keeps one notification style across a page the fan reads as one product
   * rather than two.
   */
  notify(message: string, kind?: "info" | "error"): void;
  getPlaybackPositionMs(): number;
  getGameClock(): string | null;
  /**
   * Playback control, for watch party sync. Everything above this is the
   * layer reading the page; this is the layer acting on it, and it exists
   * because a watch party is other people's play, pause and seek arriving
   * here and having to reach this player.
   *
   * Optional: the remote also runs standalone with no player at all, and
   * checks for this before offering anyone the controls.
   */
  player?: {
    getPositionMs(): number;
    isPlaying(): boolean;
    isStalled(): boolean;
    play(): void;
    pause(): void;
    seekMs(positionMs: number): void;
    // Sync corrects small drift by nudging the rate rather than seeking,
    // because a seek is visible and a 2% rate change is not. Only a drift too
    // large to close that way becomes a seek.
    getPlaybackRate(): number;
    setPlaybackRate(rate: number): void;
    // Group mute and volume commands land here.
    mute(): void;
    unmute(): void;
    /** 0 silent, 1 loudest. The player's own scale is this hook's problem. */
    setVolume(volume: number): void;
    /**
     * Loads `url` as a stitched reel if it is not already the loaded one, seeks
     * to `offsetMs` into it, and plays. Resolves true when the player accepted
     * it, false when there is no player to hand it to.
     */
    playReel(url: string, offsetMs: number): Promise<boolean>;
    /** Puts the game back and resumes where the fan left it. */
    restoreGame(): Promise<void>;
    /**
     * Back to the live edge, or back to the game when a highlight is playing.
     *
     * Distinct from restoreGame, which only undoes a takeover and does nothing
     * at all during ordinary play. `isLive` comes back in the result because
     * the caller may be a phone that cannot see the video, and needs it to
     * label its own control. See the implementation for the three outcomes and
     * why only one of them is a failure.
     */
    backToLive(): Promise<{ ok: boolean; code: string; isLive: boolean }>;
    /**
     * Play a fan's own clip: the right broadcast, the game rather than a reel,
     * then the seek.
     *
     * One method rather than a restore/switch/seek/play sequence, because the
     * order matters and only the implementation can see the reel state and the
     * player lifecycle that decide it. `feed` is the broadcast the clip was
     * made on, and changing to it REPLACES THE PLAYER on this page, so a seek
     * issued beside the switch would race a player being torn down. Omitted
     * means "whatever is showing", which is what every play did before feeds
     * were recorded.
     *
     * Resolves false when there is no player. See the implementation.
     */
    playClipAt(startMs: number, feed?: AudienceLayerFeed): Promise<boolean>;
    /**
     * Marks on the progress bar for this game's AI goals, replacing any set
     * before; an empty list clears them. The layer owns the list, this page
     * owns the bar. A click calls `onSelect(id)` and the layer plays that goal
     * and the ones after it, as pressing its card does. Present only for a
     * tenant with config.aiMoments.
     */
    setTimelineMarkers?(markers: TimelineMarker[], onSelect: (id: string) => void): void;
    /** Whether the game is live: the player's own signal ORed with the page's. */
    isLive(): boolean;
    /**
     * The frame on screen right now as a small JPEG data url, or null when
     * there is none to read. This is what a new clip's thumbnail is made of.
     */
    captureFrame(): string | null;
  };
  /**
   * Renews this app's session and hands back the new token, or null when it
   * cannot. The layer calls its own API with our token and has no session of
   * its own, so an expired one is ours to fix.
   */
  refreshAuth(): Promise<string | null>;
  /**
   * The clip buttons under the player, reported rather than acted on.
   *
   * The buttons stay where they have always been; the Audience Layer performs
   * the write. That split is deliberate: this page used to POST the clip into
   * the legacy store itself, and a layer that also created one would produce
   * two records of one clip.
   */
  clipRequests: {
    /** Returns the function that stops listening. */
    subscribe(handler: (type: string) => void): () => void;
  };
  /**
   * Where this context is mounted. Fixed at "panel" today: the only host
   * that exists is the Watch page's sidebar slot. A second surface (the
   * paired phone's own standalone view) would report itself differently, so
   * the remote can tell the two apart without a second contract.
   */
  surface: "panel";
  /**
   * The "hand this to my phone" affordance, and the reason it can be
   * missing: undefined on a build or a tenant that has not wired up
   * onRequestPairing, so the remote knows to leave the button off rather
   * than offer a control that does nothing when pressed.
   */
  secondScreen?: { onRequestPairing: () => void };
}

/** Bitmovin's player, as far as this hook uses it. */
type PlayerLike =
  | {
      getCurrentTime?: (mode?: string) => number;
      isPlaying?: () => boolean;
      isStalled?: () => boolean;
      play?: () => void;
      pause?: () => void;
      seek?: (time: number) => void;
      isLive?: () => boolean;
      getMaxTimeShift?: () => number;
      timeShift?: (time: number) => void;
      getPlaybackSpeed?: () => number;
      setPlaybackSpeed?: (speed: number) => void;
      mute?: () => void;
      unmute?: () => void;
      setVolume?: (volume: number) => void;
      // The reel's own control surface: what is loaded, loading something else
      // in its place, and the events a one-shot "reel finished" listener needs.
      getSource?: () => unknown;
      load?: (source: unknown) => Promise<void>;
      on?: (event: string, handler: () => void) => void;
      off?: (event: string, handler: () => void) => void;
      // The element actually decoding the game, which is where a clip's
      // thumbnail is read from.
      getVideoElement?: () => HTMLVideoElement | null;
    }
  | null
  | undefined;

interface Params {
  gameId: string;
  tenant: string;
  /** Where this tenant's auth lives, for renewing an expired session. */
  authApiUrl: string;
  apiBaseUrl?: string;
  playerRef: { current: PlayerLike };
  /**
   * The page's own opinion of whether this game is live, alongside the
   * player's. jumpToTime decides live from `playerRef.current.isLive() ||
   * gameData?.is_live`, because the player can lag behind what the page
   * already knows: a game that has gone live does not make isLive() true at
   * the same instant. This hook has to agree with both halves of that check,
   * not just the player's.
   *
   * Optional and false by default, so a caller that does not pass it (the
   * standalone harness, any test that predates this) behaves exactly as
   * before: live only when the player itself says so.
   */
  isLive?: boolean;
  /**
   * The fan's plan, for the download check below. Threaded in rather than
   * read here directly: `VideoPlayerLive` already receives it as a prop
   * (`subscriptionDetails`) and this hook has no other route to it, unlike
   * the permissions half of the same check, which travel inside the access
   * token this hook already reads.
   */
  subscription?: { plan_type_id?: number | string | null } | null;
  /**
   * Passed straight through to the context above. Read from the url by the
   * page, because the page owns the url and this hook owns the contract.
   */
  clipTarget?: { momentId: string; tab: "mine" | "shared" | null } | null;
  /**
   * Which broadcast the page is showing. Defaults to "home", which is both the
   * single-feed case and what every clip made before this was treated as.
   */
  feed?: AudienceLayerFeed;
  /**
   * Put the page on a different broadcast, resolving once its player is ready
   * to be seeked.
   *
   * The page owns this because it owns the source: switching feed swaps the
   * url that BitMovingPlayer is mounted with, which tears the player down and
   * builds a new one, so the only place that can say "the new one is ready" is
   * the page that rendered it.
   *
   * Optional. Without it a clip from the other feed plays where it can, which
   * is what this page did before, rather than not playing at all.
   */
  switchFeed?: (feed: AudienceLayerFeed) => Promise<void>;
  /** The story for this game, already resolved to pre or post. See useGameSummary. */
  gameSummary?: string | null;
  /** Whether that story is the preview or the report. See gameSummaryKind. */
  gameSummaryKind?: "preview" | "summary";
  /** Brand colours by team abbreviation, built by the page from its game record. */
  teamColors?: TeamColors;
  /**
   * Opens the second-screen pairing dialog (Task 9's
   * useSecondScreenSession().requestPairing). Optional: a caller that has
   * not wired up pairing (the standalone harness, any test that predates
   * this) gets no `secondScreen` on the returned context, and the remote
   * leaves the "hand this to my phone" button off rather than offering one
   * that does nothing when pressed.
   */
  onRequestPairing?: () => void;
  /**
   * Where the layer's AI goal marks go: the page keeps them and draws them on
   * the progress bar. Omitted for a tenant without AI Moments, which leaves
   * setTimelineMarkers off the player and the layer drawing nothing.
   */
  onTimelineMarkers?: (markers: TimelineMarker[], onSelect: (id: string) => void) => void;
}

/**
 * One AI goal on the progress bar, as the layer hands it over (its own
 * TimelineMarker in src/chat/types.ts). Times are on the player's timeline,
 * already chosen for live or recorded.
 */
export interface TimelineMarker {
  id: string;
  /** Where the clip starts. */
  startMs: number;
  /** The goal itself: where the mark is drawn. */
  atMs: number;
  endMs: number;
  title: string;
  /** "1st 14:31 · 0-1" */
  detail: string;
  thumbnailUrl: string | null;
  /** Waiting for a reviewer: drawn so it cannot pass for a confirmed goal. */
  pending: boolean;
}

/**
 * Builds the context the Audience Layer needs from what the Watch page already
 * knows: who is signed in, which game is on screen, and where playback is.
 *
 * The result is memoized on the values the remote actually keys off. It must
 * be: the remote memoizes its API clients on the context object's identity, so
 * a new object every render would rebuild them and re-fire their effects.
 */
export function useAudienceLayerHost({
  gameId,
  tenant,
  authApiUrl,
  apiBaseUrl,
  playerRef,
  isLive = false,
  subscription,
  clipTarget = null,
  feed = "home",
  switchFeed,
  gameSummary = null,
  gameSummaryKind = "summary",
  teamColors = EMPTY_TEAM_COLORS,
  onRequestPairing,
  onTimelineMarkers,
}: Params): {
  context: AudienceLayerHostContext | null;
  emitClipRequest: (type: string) => void;
  /**
   * What the player is showing instead of the plain game, or null.
   *
   * For the badge over the video. Covers playback started on this page and
   * playback a paired phone asked for alike, because both go through the same
   * player methods that set it.
   */
  nowPlaying: "highlight" | "clip" | null;
} {
  const auth = getCurrentAuth();
  const accessToken = auth?.AccessToken;
  const userId = auth?.UserID;

  // The getters are read on every message the fan sends, long after this hook
  // ran, so they close over a ref rather than over a rendered value. Keeping
  // the ref in a ref of our own keeps the returned object stable even if the
  // player is swapped out (theatre mode remounts it).
  const player = useRef(playerRef);
  player.current = playerRef;

  /*
   * Held in a ref, not rebuilt per render.
   *
   * The layer subscribes when the context's identity changes, so an emitter
   * that was a new object each time would leave the layer holding a
   * subscription to a set nothing publishes into.
   */
  const clipListeners = useRef(new Set<(type: string) => void>());

  const clipRequests = useRef({
    subscribe(handler: (type: string) => void) {
      clipListeners.current.add(handler);
      return () => { clipListeners.current.delete(handler); };
    },
  });

  const emitClipRequest = useCallback((type: string) => {
    for (const listener of clipListeners.current) listener(type);
  }, []);

  /*
   * What the game was playing before a highlight took the player over, and which
   * reel is loaded now.
   *
   * A ref rather than state: nothing renders off it, and putting it in state
   * would rebuild the context object, which the remote memoizes its API clients
   * on.
   */
  const reel = useRef<{
    loadedUrl: string | null;
    gameSource: unknown | null;
    gamePositionSec: number;
    gameWasLive: boolean;
  }>({ loadedUrl: null, gameSource: null, gamePositionSec: 0, gameWasLive: false });

  /*
   * Back to the game, at the point the fan left it.
   *
   * A live game goes back to the live edge rather than to the second they were
   * at: a highlight takes time to watch, and resuming behind the play would put
   * them further behind than they were.
   */
  /**
   * Puts the game's own source back in the player, and nothing else.
   *
   * Split out from restoreGame because its two callers want the video back for
   * different reasons and land in different places afterwards: restoreGame
   * returns the fan to where they were, while playClipAt seeks straight to a
   * clip. Sharing the seek as well made playing a clip a visible double jump,
   * to the old position and then to the clip, and an extra buffer with it.
   *
   * Returns whether anything was actually put back, so a caller can tell a
   * takeover being undone from ordinary play, where reloading would interrupt
   * a fan who is simply watching.
   */
  /*
   * What the player is showing instead of the plain game, or null.
   *
   * STATE, unlike `reel` above, because something renders from it: the badge
   * over the video naming what is playing. It is set by the same three methods
   * that move the player, so it covers playback started here AND playback a
   * paired phone asked for, which both go through them.
   */
  const [nowPlaying, setNowPlaying] = useState<"highlight" | "clip" | null>(null);

  const restoreGameSource = useCallback(async (): Promise<boolean> => {
    const p = player.current?.current;
    if (!p || reel.current.loadedUrl === null) return false;

    const { gameSource } = reel.current;
    reel.current.loadedUrl = null;

    if (gameSource === null) return false;
    await p.load?.(gameSource);
    return true;
  }, []);

  const restoreGame = useCallback(async (): Promise<void> => {
    if (!(await restoreGameSource())) return;
    setNowPlaying(null);

    const p = player.current?.current;
    if (!p) return;

    if (reel.current.gameWasLive) {
      // 0 is the live edge in Bitmovin's timeShift scale.
      p.timeShift?.(0);
    } else {
      p.seek?.(reel.current.gamePositionSec);
    }
    p.play?.();
  }, [restoreGameSource]);

  /*
   * Back to the live edge, whether or not a highlight took the player over.
   *
   * restoreGame above cannot serve this on its own. It returns early when
   * nothing was ever taken over, which is the ordinary case during play, so a
   * fan who has simply time-shifted behind the play and asks for the live edge
   * would press this and get nothing at all.
   *
   * Three outcomes, and only one of them is a failure. A VOD game with no reel
   * loaded has no live edge to go to and nothing to put back, so there is
   * genuinely nothing to do: that reports ok, with a code the caller can say
   * out loud, rather than an error for a request that was not wrong.
   *
   * `isLive` travels back in the result because the phone that asked cannot
   * see the video: it is how the second screen learns to label its own control
   * "Back to Live" rather than "Back to the game", without any separate status
   * channel. Live is decided the same way positionMs and seekMs decide it,
   * player OR page, because the player lags behind a game that has just gone
   * live.
   */
  /*
   * Play a fan's own clip: back to the game first, then seek into it.
   *
   * THE ORDER IS THE WHOLE POINT. A clip's start is an offset into the GAME's
   * video, so seeking to it while a highlight reel is loaded lands inside the
   * reel at a position that means nothing. restoreGame puts the game back, and
   * only then does the offset mean what it says.
   *
   * One method rather than leaving a caller to make three calls in the right
   * order, because only here are the reel state and the player in the same
   * place. The live branch also has to read getMaxTimeShift AFTER the restore:
   * reloading the source resets the time-shift window, and a value read before
   * the reload would put the seek somewhere else entirely.
   *
   * restoreGame itself returns early when no reel was ever loaded, so during
   * ordinary play this is exactly a seek and nothing more.
   */
  const playClipAt = useCallback(async (
    startMs: number,
    clipFeed?: AudienceLayerFeed,
  ): Promise<boolean> => {
    if (!player.current?.current) return false;

    /*
     * THE BROADCAST FIRST, because everything below is measured against it.
     *
     * A clip's start_ms is an offset into the feed it was made on. Seeking to
     * it on the other feed lands at the right second of the wrong broadcast,
     * which is the league's report: an away clip playing back with the home
     * commentary, and downloading the same way.
     *
     * Awaited, and the page resolves it only once the new player is ready.
     * Switching feed changes the url BitMovingPlayer is mounted with, which
     * unloads that player and constructs another, so a seek issued before this
     * settles is a seek against a player that is being thrown away.
     *
     * Skipped when the feed already matches, which is every play on a game
     * called once. A page that gave us no switchFeed keeps the old behaviour
     * (play where you are) rather than refusing, because that page has one
     * feed and the question does not arise.
     */
    if (clipFeed !== undefined && clipFeed !== feed && switchFeed) {
      await switchFeed(clipFeed);
      // The reel belonged to the feed we just left, and its saved game source
      // is that feed's url. Cleared so restoreGameSource below does not put
      // the old broadcast back over the one we just switched to.
      reel.current.loadedUrl = null;
      reel.current.gameSource = null;
    }

    // The source only, not restoreGame: that would seek to where the fan left
    // the game before this seeks to the clip, which is two jumps and two
    // buffers to reach one place. A no-op during ordinary play, and after a
    // feed switch above, which clears the reel itself.
    await restoreGameSource();

    // Re-read through the ref: the line above may have reloaded the source.
    const p = player.current?.current;
    if (!p) return false;

    if (p.isLive?.() === true || isLive) {
      const maxShift = p.getMaxTimeShift?.() ?? 0;
      p.timeShift?.(maxShift + startMs / 1000);
    } else {
      p.seek?.(startMs / 1000);
    }
    p.play?.();
    setNowPlaying("clip");
    return true;
  }, [isLive, restoreGameSource, feed, switchFeed]);

  const backToLive = useCallback(async (): Promise<{ ok: boolean; code: string; isLive: boolean }> => {
    const p = player.current?.current;
    if (!p) return { ok: false, code: "no_player", isLive: false };

    const live = p.isLive?.() === true || isLive;

    if (reel.current.loadedUrl !== null) {
      // Delegated rather than reimplemented, so the rule that a live game
      // resumes at the live edge and not where the fan left it stays in one
      // place.
      await restoreGame();
      return { ok: true, code: "restored_game", isLive: live };
    }

    if (live) {
      p.timeShift?.(0);
      setNowPlaying(null);
      return { ok: true, code: "at_live_edge", isLive: true };
    }

    setNowPlaying(null);
    return { ok: true, code: "already_at_game", isLive: false };
  }, [isLive, restoreGame]);

  /*
   * Whether this fan may download, decided here because only the host knows
   * their plan. The widget renders the control and the upgrade dialog off this
   * one boolean and never learns what a plan is.
   *
   * The rule itself now lives in src/utils/audienceLayerEntitlement.ts, not
   * here: the Studio page (v2) needs the same check and cannot import from
   * src/Pages, so it moved to where both halves of the app can reach it. See
   * that module for why it stays a courtesy rather than the gate.
   */
  const canDownload = useMemo(
    () => canDownloadFrom({ token: accessToken, subscription }),
    // subscription?.plan_type_id rather than subscription itself:
    // canDownloadFrom only ever reads that one field, and a caller handing
    // over a fresh subscription object each render would otherwise re-run
    // this decode and JSON parse of the access token every render for no
    // change in the result.
    [accessToken, subscription?.plan_type_id],
  );

  const context = useMemo(() => {
    // Signed out, so there is nobody to attribute messages or reactions to.
    // Returning null lets the caller render nothing rather than mount a panel
    // that would 401 on its first request.
    if (!accessToken || userId === undefined || userId === null || userId === "") return null;

    const name = [auth?.FirstName, auth?.LastName].filter(Boolean).join(" ").trim();

    /*
     * Where playback is, in the game's own terms. Live, on demand and inside a
     * reel alike, this is what getCurrentTime("relativetime") already reports,
     * and there is deliberately no branch here.
     *
     * This function used to correct for the live edge, and that correction was
     * the league's wrong-time bug. The reasoning behind it was that Bitmovin
     * counts live playback backwards from the live edge, so the position in
     * the game was the distance from the far end of the time-shift window,
     * `seconds - getMaxTimeShift()`. The premise is false. It is getTimeShift()
     * that is negative at the live edge; TimeMode.RelativeTime is documented as
     * defining "the beginning of the DVR window at the time of tuning in" as 0,
     * so it already counts forward from the start of the broadcast.
     *
     * The correction therefore added the window on top of a number that had
     * already measured it. Our streams are #EXT-X-PLAYLIST-TYPE:EVENT, whose
     * window reaches back to the game's first segment and only grows, so at the
     * live edge getMaxTimeShift() is the exact negative of the position and the
     * result was precisely double. One fan's Save at 1:24:31 was stored at
     * 2:49:03, past the end of the asset, and the cut clamped to zero and
     * returned the broadcast from the top.
     *
     * The legacy page is the reference: handleClipPost in index.tsx has cut
     * clips from `getCurrentTime("relativetime") - 5` since 2023 with no window
     * arithmetic, and skipForward reads the distance to the live edge as
     * `-position - maxTimeShift`, which is only the right way round if position
     * counts forward. seekMs below is the other half of the round trip and was
     * already correct, so this reading is what had to move to meet it.
     *
     * Clamped at zero because start_ms is unsigned at the API, and a player
     * that reports a small negative while it settles should stamp the start of
     * the game rather than be rejected.
     */
    const positionMs = () => {
      const p = player.current?.current;
      if (!p) return 0;

      const seconds = p.getCurrentTime?.("relativetime");
      if (!Number.isFinite(seconds)) return 0;

      return Math.max(0, Math.round((seconds as number) * 1000));
    };

    return {
      gameId,
      tenant,
      apiBaseUrl,
      device: "web",
      /*
       * Whether the game is live, decided the same way every other branch in
       * this hook decides it: the player's own signal ORed with the page's,
       * because the player lags behind a game that has just gone live.
       *
       * The layer cannot work this out for itself, and it needs it to label a
       * control: a live game has a live edge to return to, a recorded one has
       * only the game.
       */
      isLive: playerRef.current?.isLive?.() === true || isLive,
      canDownload,
      gameSummary,
      gameSummaryKind,
      teamColors,
      /*
       * The layer's messages go through this app's own toasts, so a fan sees
       * one notification style across a page they read as one product. The
       * layer says what; this says where.
       */
      notify: (message: string, kind: "info" | "error" = "info") => {
        if (kind === "error") toast.error(message);
        else toast.info(message);
      },
      user: {
        id: String(userId),
        // Falling back to the email's local part rather than to "Anonymous":
        // a name is what other fans see next to every message.
        name: name || auth?.Email?.split("@")[0] || `Fan ${userId}`,
        jwt: accessToken,
      },
      getPlaybackPositionMs: positionMs,

      /*
       * One refresh path for the whole page. This is the same single-flight
       * call the app's own axios interceptor uses, so a burst of 401s from the
       * layer and the app together still makes one request, and both end up
       * holding the same new token.
       */
      refreshAuth: () => refreshAuthToken(authApiUrl),

      /*
       * The watch party's end of the player. Every method reaches through the
       * ref at call time rather than closing over a player instance, because
       * theatre mode remounts the player and a captured instance would go on
       * driving a video nobody is watching.
       *
       * Each is guarded: Bitmovin is loaded asynchronously and swapped between
       * implementations on this page, so a control can genuinely be absent for
       * a moment, and a party member's seek arriving in that window should do
       * nothing rather than throw inside the remote.
       */
      player: {
        getPositionMs: positionMs,
        isPlaying: () => player.current?.current?.isPlaying?.() === true,
        isStalled: () => player.current?.current?.isStalled?.() === true,
        play: () => { player.current?.current?.play?.(); },
        pause: () => { player.current?.current?.pause?.(); },
        /*
         * Seconds at the player, milliseconds in the contract, and two
         * different calls depending on whether this is live.
         *
         * seek() on a live stream does not do what a Moment needs: the timeline
         * is anchored to the live edge, so the same call has to become a
         * timeShift measured from the far end of the window. This is the same
         * branch the legacy page's jumpToTime has always taken.
         */
        seekMs: (ms: number) => {
          const p = player.current?.current;
          if (!p) return;

          if (p.isLive?.() === true || isLive) {
            const maxShift = p.getMaxTimeShift?.() ?? 0;
            p.timeShift?.(maxShift + ms / 1000);
            return;
          }

          p.seek?.(ms / 1000);
        },
        /*
         * The frame on screen right now, small, as a JPEG data url.
         *
         * This is a clip's thumbnail. The fan is watching the play as they
         * press the button, so the player has already decoded exactly the
         * frame they mean: reading it back costs one canvas and no network at
         * all, where generating it afterwards would mean seeking a three hour
         * stream server-side to a moment nobody is watching any more.
         *
         * Sized down here rather than anywhere later. The tile is 112 pixels
         * wide, so 320 covers it on any display worth the pixels, and it keeps
         * the create request to a few tens of kilobytes.
         */
        captureFrame: () => {
          const video = player.current?.current?.getVideoElement?.();
          // HAVE_CURRENT_DATA. Anything below it means there is no frame to
          // read yet, and drawing would paint an empty rectangle.
          if (!video || video.readyState < 2) return null;

          const { videoWidth: width, videoHeight: height } = video;
          if (width === 0 || height === 0) return null;

          try {
            const canvas = document.createElement("canvas");
            canvas.width = THUMBNAIL_WIDTH;
            canvas.height = Math.round((THUMBNAIL_WIDTH * height) / width);
            const ctx = canvas.getContext("2d");
            if (!ctx) return null;

            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            return canvas.toDataURL("image/jpeg", 0.6);
          } catch {
            /*
             * A canvas that has drawn cross-origin video is tainted, and
             * toDataURL throws SecurityError rather than returning anything.
             * Whether that happens depends on how the browser is playing the
             * stream: through MSE the media is fetched by script and the
             * element is not cross-origin, while a native HLS playback can
             * taint it.
             *
             * So this is a real possibility on some browsers and not an error
             * in any of them: no thumbnail, the same as a fan clipping before
             * the first frame decodes, and the card shows its type glyph.
             */
            return null;
          }
        },

        // Bitmovin calls it speed; the contract calls it rate, because that is
        // what the sync SDK calls it. 1 when the player cannot say, which
        // reads as "normal" rather than as a rate of zero.
        getPlaybackRate: () => player.current?.current?.getPlaybackSpeed?.() ?? 1,
        setPlaybackRate: (rate: number) => { player.current?.current?.setPlaybackSpeed?.(rate); },
        mute: () => { player.current?.current?.mute?.(); },
        unmute: () => { player.current?.current?.unmute?.(); },
        // The contract is 0 to 1 because that is what the sync SDK sends.
        // Bitmovin's own scale is 0 to 100, and this is the only place that
        // difference should ever be visible.
        setVolume: (volume: number) => {
          const clamped = Math.min(1, Math.max(0, volume));
          player.current?.current?.setVolume?.(Math.round(clamped * 100));
        },

        /*
         * A highlight in the page's own player, as one stitched reel.
         *
         * The reel is loaded once and every later press is a seek into it, which is
         * what the legacy Highlights tab did and why it felt instant: a reload costs a
         * couple of seconds and restarts the video.
         *
         * Deliberately NOT routed through seekMs. That call branches on
         * `p.isLive() || isLive` and turns a seek into a timeShift from the live edge.
         * A reel is a VOD playlist, so the player reports VOD, but the page's own
         * isLive flag is still true while the game runs: the branch would fire on the
         * page's half of the condition and seek nowhere. A reel's timeline starts at
         * zero and a plain seek is always right for it.
         */
        playReel: async (url: string, offsetMs: number): Promise<boolean> => {
          const p = player.current?.current;
          if (!p) return false;

          if (reel.current.loadedUrl !== url) {
            // Remembered before the load, because after it the player is holding the
            // reel and can no longer say what the game was.
            if (reel.current.loadedUrl === null) {
              reel.current.gameSource = p.getSource?.() ?? null;
              reel.current.gameWasLive = p.isLive?.() === true || isLive;
              const seconds = p.getCurrentTime?.("relativetime");
              reel.current.gamePositionSec = Number.isFinite(seconds) ? (seconds as number) : 0;
            }

            // One shot: the reel returns the fan to the game when it runs out, and a
            // listener left attached would fire again on the game's own end.
            // "playbackfinished" is PlayerEvent.PlaybackFinished, written as its value
            // so this hook needs no bitmovin import and its tests can drive a plain
            // object.
            const onFinished = () => {
              p.off?.("playbackfinished", onFinished);
              void restoreGame();
            };
            p.on?.("playbackfinished", onFinished);

            await p.load?.({ hls: url });
            reel.current.loadedUrl = url;
            setNowPlaying("highlight");
          }

          p.seek?.(offsetMs / 1000);
          p.play?.();
          return true;
        },

        /*
         * Back to the game, at the point the fan left it.
         *
         * A live game goes back to the live edge rather than to the second they were
         * at: a highlight takes time to watch, and resuming behind the play would put
         * them further behind than they were.
         */
        restoreGame,

        /*
         * The one above undoes a takeover; this one also serves a fan who is
         * merely behind the play. A paired phone calls it, which is why its
         * result carries isLive: that phone cannot see this video.
         */
        backToLive,

        // The clip half of the same idea: back to the game, then into it.
        playClipAt,

        // Only for a tenant with AI Moments; absent, the layer draws nothing.
        ...(onTimelineMarkers ? { setTimelineMarkers: onTimelineMarkers } : {}),

        /*
         * The live signal, decided the same way every other branch here
         * decides it. Exposed on the player rather than read off the context
         * because the second-screen executor holds this object and not the
         * context, and stamps the answer onto every playback result.
         */
        isLive: () => player.current?.current?.isLive?.() === true || isLive,
      },
      // No game clock source on this page yet. Null is part of the contract
      // and reads as "unknown", which is true, rather than 0:00, which is a
      // period start.
      getGameClock: () => null,
      clipRequests: clipRequests.current,
      clipTarget,
      // What the fan is watching right now, so a clip made in the next second
      // is stamped with it. Read per render rather than captured, because the
      // toggle under the video can move at any time.
      feed,
      surface: "panel" as const,
      /*
       * Every signed-in viewer may pair a phone (it was admins only while it
       * was exercised against real games). The layer renders the button only
       * when it is handed a `secondScreen`, so this is still the one place
       * that decides who sees it. What a paired phone may review stays with
       * the API: AI review is for admins whatever pairs.
       */
      secondScreen: onRequestPairing ? { onRequestPairing } : undefined,
    };
    // auth is re-read from localStorage on every render and is a fresh object
    // each time, so the primitives it yields are the dependencies, not it.
    // clipRequests.current is a ref and is stable, so listing it would be
    // misleading. isLive is the boolean the caller passed, not the gameData
    // object it was read from: gameData can be a new object every render, and
    // depending on it would rebuild this context (and every API client the
    // remote memoizes off its identity) far more often than the one bit that
    // actually matters ever changes.
  }, [gameId, tenant, apiBaseUrl, authApiUrl, accessToken, userId, auth?.FirstName, auth?.LastName, auth?.Email, isLive, restoreGame, backToLive, playClipAt, canDownload, gameSummary, gameSummaryKind, teamColors, clipTarget, onRequestPairing, feed, onTimelineMarkers]);

  return { context, emitClipRequest, nowPlaying };
}
